import { isNullish, isNullOrEmpty } from './null-utils';

export const allowedBaseURLs = [
  '.deckers.com',
  '.dms.deckers.com',
  '.ahnu.com',
  '.ahnu.co',
  '.teva.com',
  '.hoka.com',
  '.ugg.com',
  // Fix for feature builds.
  ...(process.env.APP_ENV !== 'prod' ? ['-deckers.vercel.app'] : []),
  // Adds the localhost to the allow list for the dev server.
  ...(process.env.APP_ENV !== 'prod' ? ['localhost'] : [])
];

export const allowedUIBaseURLs = [
  ...allowedBaseURLs,
  '.youtube.com',
  'use.typekit.net/af' // Adobe Fonts CDN
];

/**
 * Decides which allow list to use.
 */
export type AllowListType = 'base' | 'ui';

/**
 * Normalizes the url string to a URL object.
 * @param url - The url string.
 * @returns The normalized URL object.
 */
const normalizeURL = (url: string): URL => {
  let normalizedString = url;

  if (url.startsWith('//')) normalizedString.replace('//', 'https://');

  // Handles for the http protocol on the dev server.
  if (url.startsWith('http://') && process.env.APP_ENV !== 'prod') {
    return new URL(url);
  }

  if (!url.startsWith('https://')) {
    normalizedString = `https://${url}`;
  }

  return new URL(normalizedString);
};

/**
 * Handles extraction in cases where the URL is wrapped in `url()` syntax.
 *
 * Commonly used in CSS context, this regex captures main use case variations:
 * - `url("...")` or `url('...')` with optional spaces
 * - `url (...)` without quotes (with spaces between url and parenthesis)
 * - `URL("...")` or `URL ('...')` with optional spaces
 * - Strips the `url()` wrapper and any surrounding quotes or spaces.
 * - Safely handles strings with leading/trailing spaces.
 *
 * @param css - The CSS string containing the URL.
 * @returns The extracted URL or null if no valid URL is found.
 * @example
 * extractURLFromCSSPattern('  url("https://example.com")  ') // returns 'https://example.com'
 * extractURLFromCSSPattern("  url('https://example.com')  ") // returns 'https://example.com'
 * extractURLFromCSSPattern('  url  (  https://example.com  )  ') // returns 'https://example.com'
 * extractURLFromCSSPattern('  URL  (  "https://example.com"  )  ') // returns 'https://example.com'
 */
export const extractURLFromCSSPattern = (css: string): string | null => {
  // First trim the entire string to remove surrounding whitespace
  const trimmedInput = css.trim();

  // Match url(...) pattern with flexible spacing
  const urlPattern = /url\s*\(\s*(['"]?)([^)]+?)\1\s*\)/i;
  const match = urlPattern.exec(trimmedInput);

  // Return trimmed URL content, if found and not empty.
  return isNullOrEmpty(match?.[2]) ? null : match![2].trim();
};

/**
 * Tests whether the url string is an allowed url.
 * @param urlString - The url string.
 * @param type - The allow list type.
 * @returns A boolean representing whether the url is on the specified allow list.
 */
export const isURLAllowed = (
  urlString: string,
  type: AllowListType = 'base'
): boolean => {
  if (urlString.startsWith('/') && !urlString.startsWith('//')) {
    return true;
  }

  const allowList = type === 'ui' ? allowedUIBaseURLs : allowedBaseURLs;

  const { hostname } = normalizeURL(urlString);

  for (const allowedUrl of allowList) {
    if (!isNullish(allowedUrl) && hostname.includes(allowedUrl)) {
      return true;
    }
  }
  return false;
};
